// An alert kind as a sign (byj-1wv.51.10): a line icon each, drawn on the
// 24-unit grid the app's other icons use, and a tone by what it asks of the
// instructor — a turn that failed or got nowhere, red; a permission waiting,
// a call repeated, a cost spike, amber; a session gone quiet, grey. Each
// differs in shape, not colour alone. A row shows the signs, the dashboard's
// alert filters count them, a watch says each in full.
import type { ReactNode } from "react";

export type AlertTone = "fail" | "warn" | "quiet";

const tones: Record<string, AlertTone> = {
  "turn-failed": "fail",
  "no-progress": "fail",
  "permission-wait": "warn",
  "repeated-call": "warn",
  "cost-spike": "warn",
  idle: "quiet",
};

export const alertTone = (kind: string): AlertTone => tones[kind] ?? "warn";

const paths: Record<string, ReactNode> = {
  // a key: the agent waits for leave to go on
  "permission-wait": (
    <>
      <circle cx="7.5" cy="15.5" r="4" />
      <path d="M10.4 12.6 19 4M15.5 7.5l2.5 2.5M13.5 9.5l2 2" />
    </>
  ),
  // a cross in a circle: the turn failed
  "turn-failed": (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m15 9-6 6M9 9l6 6" />
    </>
  ),
  // an arrow against a wall: the turn ended without getting anywhere
  "no-progress": <path d="M3.5 12h11M11 8l4 4-4 4M19.5 5v14" />,
  // arrows round a loop: the same call again
  "repeated-call": (
    <>
      <path d="M4 11V9.5A3.5 3.5 0 0 1 7.5 6H19M16 3l3 3-3 3" />
      <path d="M20 13v1.5a3.5 3.5 0 0 1-3.5 3.5H5M8 21l-3-3 3-3" />
    </>
  ),
  // a line that jumps: a turn costing many times the usual
  "cost-spike": <path d="m3 17 5-5 3.5 3.5L20 7M14.5 7H20v5.5" />,
  // a crescent: nothing for a while
  idle: <path d="M19.5 14.5A8 8 0 1 1 9.5 4.5a6.2 6.2 0 0 0 10 10Z" />,
};

// a kind the relay adds later, before the client knows it
const fallback = <path d="M12 4 3 20h18L12 4ZM12 10v4.5M12 17.2v.3" />;

export function AlertIcon({ kind, size = 14 }: { kind: string; size?: number }) {
  return (
    <svg
      className="alert-icon"
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[kind] ?? fallback}
    </svg>
  );
}
